import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';

const { 
  FiX, FiFacebook, FiCalendar, FiUsers, 
  FiTruck, FiBriefcase, FiHeart, FiMail, 
  FiExternalLink, FiMenu, FiClock, FiInfo
} = FiIcons;

function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showMenuModal, setShowMenuModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const emailAddress = "gotthemdogs2023@gmail.com";
  const facebookUrl = "https://www.facebook.com/gotthemdogs/";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = isScrolled ? 110 : 140; 
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsMobileMenuOpen(false);
  };

  const navLinks = [
    { name: 'Fan Favorites', id: 'fan-favorites' },
    { name: 'Menu', id: 'menu' },
    { name: 'Find Us', id: 'find-us' },
    { name: 'Catering', id: 'catering' }
  ];

  const fanFavorites = [
    {
      name: "Original Hot Dog",
      description: "The classic that built the pack. Mustard | Ketchup | Relish | Onions",
      image: "https://cloud-1de12d.becdn.net/media/original/ff39148718952055d91a5e9732ffb456.jpg",
      contain: true
    },
    {
      name: "Bubba Burger",
      description: "Grilled Onions | Tomato | Lettuce | Cheese | Pickles",
      image: "https://cloud-1de12d.becdn.net/media/original/f7401d8d5001b291ebbb1c18c75458e8.jpg",
      contain: true
    },
    {
      name: "Chili Cheese Dog",
      description: "Chili | Cheese | Onion",
      image: "https://cloud-1de12d.becdn.net/media/original/762a28ddf3b177807f80e977c5ccc350.jpg",
      contain: true
    },
    {
      name: "BBQ Pulled Pork Nachos",
      description: "Cheese | Pulled Pork | Onion | Jalapeño",
      image: "https://cloud-1de12d.becdn.net/media/original/79b798d0c0244403c0e86e4a4958cbec.jpg",
      contain: true
    }
  ];

  const menuImageUrl = "https://cloud-1de12d.becdn.net/media/original/fe795b2554afde90720c02f9b2e0e121.png";

  return (
    <div className="min-h-screen bg-white overflow-x-hidden selection:bg-yellow-400 selection:text-black">
      <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-500">
        <nav className={`bg-white transition-all duration-300 ${
          isScrolled ? 'shadow-lg border-b border-gray-100 py-1' : 'py-3 md:py-5'
        }`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-14 md:h-16">
              <div className="flex-shrink-0">
                <h1 
                  className="text-xl md:text-2xl font-black text-green-800 cursor-pointer transition-transform hover:scale-105 active:scale-95 flex items-center" 
                  onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}
                >
                  Got Them <span className="text-red-600 ml-1.5">Dogs</span>
                </h1>
              </div>

              <div className="hidden lg:flex items-center space-x-6 xl:space-x-10">
                {navLinks.map((link) => (
                  <button 
                    key={link.id}
                    onClick={() => scrollToSection(link.id)} 
                    className="text-gray-700 hover:text-red-600 font-black text-xs xl:text-sm uppercase tracking-[0.15em] transition-all relative group"
                  >
                    {link.name}
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-red-600 transition-all group-hover:w-full"></span>
                  </button>
                ))}
              </div>

              <div className="hidden md:flex items-center space-x-4">
                <button 
                  onClick={() => scrollToSection('find-us')}
                  className="bg-yellow-500 text-black px-5 py-2.5 rounded-xl font-black text-xs uppercase tracking-wider hover:bg-yellow-400 transition-all duration-300 shadow-lg shadow-yellow-500/20 active:scale-95"
                >
                  Find Us Today
                </button>
                <button 
                  onClick={() => setShowMenuModal(true)}
                  className="border-2 border-green-800 text-green-800 px-5 py-2.5 rounded-xl font-black text-xs uppercase tracking-wider hover:bg-red-600 hover:border-red-600 hover:text-white transition-all duration-300 active:scale-95"
                >
                  View Menu
                </button>
              </div>

              <div className="flex md:hidden items-center space-x-3">
                <button 
                  onClick={() => scrollToSection('find-us')}
                  className="bg-yellow-500 text-black px-4 py-2 rounded-lg font-black text-[10px] uppercase tracking-widest shadow-md"
                >
                  Find Us
                </button>
                <button 
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="text-green-800 p-1.5 bg-gray-50 rounded-lg active:scale-90 transition-transform"
                >
                  <SafeIcon icon={isMobileMenuOpen ? FiX : FiMenu} className="w-6 h-6" />
                </button>
              </div>
            </div>
          </div>

          <AnimatePresence>
            {isMobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="md:hidden bg-white border-t border-gray-100 shadow-2xl overflow-hidden"
              >
                <div className="px-6 py-10 space-y-6">
                  {navLinks.map((link) => (
                    <button
                      key={link.id}
                      onClick={() => scrollToSection(link.id)}
                      className="block w-full text-left text-2xl font-black text-green-800 uppercase tracking-tight active:text-red-600"
                    >
                      {link.name}
                    </button>
                  ))}
                  <div className="pt-8 border-t border-gray-100 flex flex-col gap-4">
                    <button 
                      onClick={() => { setShowMenuModal(true); setIsMobileMenuOpen(false); }}
                      className="w-full bg-red-600 text-white py-4 rounded-2xl font-black text-center text-lg shadow-xl shadow-red-600/20"
                    >
                      View Full Menu
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </nav>

        <div 
          className="bg-green-800 text-white shadow-lg border-t border-white/5 cursor-pointer hover:bg-green-700 transition-colors py-2.5"
          onClick={() => scrollToSection('find-us')}
        >
          <div className="max-w-7xl mx-auto px-4 flex items-center justify-center">
            <div className="w-2 md:w-2.5 h-2 md:h-2.5 bg-red-600 rounded-full animate-pulse mr-2.5 border border-white/20"></div>
            <span className="text-[9px] md:text-xs font-black tracking-[0.2em] uppercase">See where we're serving today</span>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-40 md:pt-64 pb-20 md:pb-36 bg-gradient-to-br from-yellow-50 via-white to-red-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 md:gap-24 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center lg:text-left"
            >
              <h1 className="text-5xl sm:text-6xl lg:text-8xl font-black text-green-800 mb-8 leading-[1] tracking-tighter">
                Gourmet Dogs.<br />
                <span className="text-yellow-500 relative inline-block mt-2">
                  Big Flavor.
                  <span className="absolute -bottom-2 left-0 w-full h-2 md:h-4 bg-red-600 rounded-full opacity-80"></span>
                </span>
              </h1>
              <p className="text-lg md:text-2xl text-gray-700 mb-12 font-medium max-w-xl mx-auto lg:mx-0 leading-relaxed">
                Rolling through Southeast Texas with the best hot dogs you've ever tasted. We don't just serve dogs; we serve legends.
              </p>
              <div className="flex flex-col sm:flex-row justify-center lg:justify-start gap-5 mb-10">
                <motion.button
                  onClick={() => scrollToSection('find-us')}
                  className="bg-yellow-500 text-black px-12 py-5 rounded-2xl font-black text-xl hover:bg-yellow-400 shadow-2xl shadow-yellow-500/20 transition-all"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Find Us Today
                </motion.button>
                <motion.button
                  onClick={() => setShowMenuModal(true)}
                  className="border-2 border-green-800 text-green-800 px-12 py-5 rounded-2xl font-black text-xl hover:bg-red-600 hover:border-red-600 hover:text-white transition-all shadow-lg"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  View Menu
                </motion.button>
              </div>
              <div className="flex items-center justify-center lg:justify-start space-x-4">
                <div className="w-12 h-1 bg-red-600 rounded-full"></div>
                <p className="text-xs md:text-sm text-gray-400 font-black uppercase tracking-[0.3em]">Serving Southeast Texas</p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1 }}
              className="relative px-4 sm:px-12 lg:px-0 flex justify-center lg:justify-end"
            >
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-yellow-400/10 rounded-full blur-[120px] -z-10"></div>
              <motion.img
                src="https://cloud-1de12d.becdn.net/media/original/2d5330ce061eef0d807999ba352a4e9c.png"
                alt="Got Them Dogs Gourmet Hot Dog Truck"
                className="w-full max-w-[340px] md:max-w-[500px] h-auto drop-shadow-[0_30px_50px_rgba(0,0,0,0.15)]"
                animate={{ y: [0, -15, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Fan Favorites Section */}
      <section id="fan-favorites" className="py-24 md:py-36 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16 md:mb-24"
          >
            <span className="text-red-600 font-black tracking-[0.4em] uppercase text-xs mb-4 block">The Best Sellers</span>
            <h2 className="text-5xl md:text-7xl font-black text-green-800 mb-6 inline-block relative">
              Fan Favorites
              <div className="h-2.5 w-40 bg-red-600 mx-auto mt-5 rounded-full"></div>
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
            {fanFavorites.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-[2.5rem] shadow-[0_15px_40px_rgba(0,0,0,0.06)] overflow-hidden hover:shadow-[0_30px_60px_rgba(0,0,0,0.12)] transition-all duration-500 border border-gray-50 flex flex-col h-full group"
              >
                <div className={`relative overflow-hidden aspect-[4/3] ${item.contain ? 'bg-gray-50/50' : 'bg-gray-100'}`}>
                  <img
                    src={item.image}
                    alt={`${item.name} - Got Them Dogs Signature`}
                    className={`w-full h-full ${item.contain ? 'object-contain p-6' : 'object-cover'} group-hover:scale-110 transition-transform duration-700 ease-out`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </div>
                <div className="p-10 flex-1 flex flex-col">
                  <h3 className="text-2xl font-black text-green-800 mb-4 group-hover:text-red-600 transition-colors">{item.name}</h3>
                  <p className="text-gray-500 text-base leading-relaxed font-medium mb-4">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Menu Section */}
      <section id="menu" className="py-24 md:py-40 bg-gray-50 border-y border-gray-100">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-5xl md:text-8xl font-black text-green-800 mb-10 tracking-tighter uppercase italic">The Full Spread</h2>
            <p className="text-xl md:text-3xl text-gray-600 mb-14 max-w-2xl mx-auto font-medium leading-tight">
              From classic favorites to wild creations, we've got something to satisfy every craving. 
            </p>
            <motion.button
              onClick={() => setShowMenuModal(true)}
              className="bg-yellow-500 text-black px-16 py-6 rounded-[2rem] font-black text-2xl hover:bg-yellow-400 shadow-2xl shadow-yellow-500/30 transition-all active:scale-95"
              whileHover={{ scale: 1.05 }}
            >
              View Full Menu
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Find Us Section */}
      <section id="find-us" className="py-24 md:py-36 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16 md:mb-24"
          >
            <h2 className="text-5xl md:text-7xl font-black text-green-800 mb-8 uppercase tracking-tighter">Where We're Rolling</h2>
            <div className="h-3 w-48 bg-red-600 mx-auto mb-10 rounded-full"></div>
            <p className="text-xl md:text-3xl text-gray-600 font-medium">
              Locations update regularly.<br className="md:hidden" />
              <span className="text-red-600 font-black underline decoration-yellow-500 underline-offset-8 decoration-8">Check back before you drive.</span>
            </p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-5xl mx-auto"
          >
            <div className="relative bg-white rounded-[3rem] overflow-hidden shadow-[0_30px_70px_rgba(0,0,0,0.12)] border border-gray-100">
              <div className="bg-gray-50 border-b border-gray-100 py-6 px-8 md:px-12 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-red-600 rounded-2xl flex items-center justify-center shadow-lg shadow-red-600/30">
                    <SafeIcon icon={FiCalendar} className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-base md:text-xl font-black text-green-800 uppercase tracking-wider">Live Schedule</h3>
                    <div className="flex items-center space-x-2">
                      <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-[10px] md:text-xs font-bold text-gray-400 uppercase tracking-widest">Real-time Updates</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative w-full aspect-[4/5] sm:aspect-[16/11] md:aspect-[16/9] lg:aspect-[21/9]">
                <iframe 
                  src="https://calendar.google.com/calendar/embed?src=57d8dca728c36eedc52c0823e0f948b3668ff814d9f0c41031624beecaf847b9%40group.calendar.google.com&ctz=America%2FChicago" 
                  className="absolute inset-0 w-full h-full border-0"
                  frameBorder="0" 
                  scrolling="no"
                  title="Got Them Dogs Location Calendar - Serving SETX"
                ></iframe>
              </div>

              <div className="bg-gray-50 border-t border-gray-100 py-6 px-10 flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center space-x-3 text-gray-400">
                  <SafeIcon icon={FiInfo} className="w-5 h-5 text-red-600" />
                  <p className="text-[10px] md:text-xs font-black uppercase tracking-[0.2em] italic">Events subject to weather conditions</p>
                </div>
                <button 
                  onClick={() => scrollToSection('catering')}
                  className="text-green-800 font-black text-xs md:text-sm uppercase tracking-[0.2em] hover:text-red-600 transition-colors flex items-center group"
                >
                  Request a specific date 
                  <SafeIcon icon={FiExternalLink} className="ml-2 w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Built for Crowds */}
      <section className="py-24 md:py-40 bg-green-800 text-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-3 bg-red-600"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20 md:mb-32"
          >
            <h2 className="text-5xl md:text-8xl font-black mb-8 tracking-tighter uppercase italic">Built for Crowds</h2>
            <div className="h-2 w-32 bg-yellow-500 mx-auto rounded-full"></div>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-12 md:gap-16">
            {[
              { icon: FiUsers, title: "Festivals", desc: "Main stage energy, main course flavor." },
              { icon: FiHeart, title: "Private Events", desc: "Your celebration, our gourmet dogs." },
              { icon: FiBriefcase, title: "Corporate", desc: "Upgrade your office lunch game." },
              { icon: FiTruck, title: "Catering", desc: "Full-service events, zero compromise." }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group text-center"
              >
                <div className="bg-yellow-500 w-24 h-24 rounded-[2.5rem] flex items-center justify-center mx-auto mb-10 group-hover:bg-red-600 transition-all duration-500 shadow-2xl transform group-hover:rotate-12 group-hover:scale-110">
                  <SafeIcon icon={item.icon} className="w-10 h-10 text-black group-hover:text-white transition-colors duration-500" />
                </div>
                <h3 className="text-3xl font-black mb-5 uppercase tracking-wide">{item.title}</h3>
                <p className="text-green-100 text-lg font-medium leading-relaxed opacity-80">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Catering Section */}
      <section id="catering" className="py-24 md:py-48 bg-white relative">
        <div className="max-w-5xl mx-auto px-4 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-6xl md:text-9xl font-black text-green-800 mb-10 italic uppercase tracking-tighter leading-none">
              Book The <span className="text-red-600">Dogs</span>
            </h2>
            
            <p className="text-2xl md:text-4xl text-gray-700 font-bold mb-16 max-w-3xl mx-auto leading-snug">
              Festivals, Weddings, or Corporate.<br />
              <span className="text-red-600">We bring the flavor, you bring the pack.</span>
            </p>

            <div className="flex justify-center items-center">
              <motion.a
                href={`mailto:${emailAddress}?subject=Booking Inquiry for Got Them Dogs`}
                className="group relative inline-flex items-center justify-center"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <div className="bg-yellow-500 text-black px-16 md:px-28 py-8 md:py-10 rounded-[120px] font-black text-2xl md:text-5xl shadow-2xl shadow-yellow-500/30 transition-all duration-300 relative overflow-hidden border-4 border-yellow-600 active:bg-yellow-400">
                  <div className="absolute top-1/2 left-0 w-full h-2 md:h-3 bg-red-600 -translate-y-1/2 opacity-20 group-hover:opacity-40 transition-opacity"></div>
                  <span className="relative z-10 flex items-center uppercase tracking-tighter">
                    Email The Pack
                    <SafeIcon icon={FiMail} className="ml-5 md:ml-8 w-10 h-10 md:w-16 md:h-16 transform group-hover:rotate-12 transition-transform" />
                  </span>
                </div>
              </motion.a>
            </div>
            
            <p className="mt-12 text-lg md:text-2xl font-black text-green-800/40 uppercase tracking-[0.3em]">
              {emailAddress}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-green-800 text-white py-20 md:py-32 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-3 bg-red-600 opacity-50"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-16 md:gap-20">
            <div className="text-center sm:text-left">
              <h3 className="text-3xl md:text-4xl font-black mb-8 italic tracking-tighter">
                Got Them <span className="text-red-600 ml-1">Dogs</span>
              </h3>
              <p className="text-green-100 font-bold uppercase tracking-widest text-sm leading-relaxed">
                Gourmet Hot Dogs & Good Times. <br />
                The Legend of SETX.
              </p>
            </div>
            
            <div className="text-center sm:text-left">
              <h4 className="font-black text-yellow-500 uppercase text-xs tracking-[0.4em] mb-10">Contact</h4>
              <div className="space-y-6">
                <a href={`mailto:${emailAddress}`} className="flex items-center justify-center sm:justify-start group cursor-pointer hover:text-yellow-400 transition-colors duration-300">
                  <SafeIcon icon={FiMail} className="w-6 h-6 mr-4 text-red-500 group-hover:scale-110 transition-transform" />
                  <span className="text-xl font-black tracking-tight">{emailAddress}</span>
                </a>
              </div>
            </div>

            <div className="text-center sm:text-left">
              <h4 className="font-black text-yellow-500 uppercase text-xs tracking-[0.4em] mb-10">Follow</h4>
              <div className="flex justify-center sm:justify-start">
                {/* Refined Small Facebook Button */}
                <a 
                  href={facebookUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-white/5 hover:bg-red-600 border border-white/10 px-4 py-2.5 rounded-xl transition-all duration-300 group active:scale-95 shadow-lg"
                >
                  <SafeIcon icon={FiFacebook} className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                  <span className="ml-3 font-black uppercase text-[10px] tracking-[0.2em]">Facebook</span>
                </a>
              </div>
            </div>

            <div className="text-center sm:text-left">
              <h4 className="font-black text-yellow-500 uppercase text-xs tracking-[0.4em] mb-10">SETX Region</h4>
              <p className="text-green-100 text-sm font-black uppercase leading-[2.2] tracking-widest opacity-80">
                Orange &bull; Bridge City &bull; Mauriceville &bull; Beaumont &bull; Silsbee &bull; Jasper
              </p>
            </div>
          </div>
          
          <div className="border-t border-white/10 mt-24 pt-12 text-center">
            <p className="text-green-200 text-xs font-bold uppercase tracking-[0.3em]">&copy; 2026 Got Them Dogs. All Rights Reserved.</p>
          </div>
        </div>
      </footer>

      {/* Menu Modal */}
      <AnimatePresence>
        {showMenuModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/90 backdrop-blur-2xl z-[60]"
              onClick={() => setShowMenuModal(false)}
            />
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 40 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 40 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-6 md:p-10 pointer-events-none"
            >
              <div className="bg-white rounded-[2.5rem] md:rounded-[4rem] shadow-[0_50px_100px_rgba(0,0,0,0.5)] max-w-5xl w-full h-full max-h-[94vh] overflow-hidden pointer-events-auto border-t-[14px] border-red-600 flex flex-col relative">
                
                <div className="flex items-center justify-between px-8 py-6 md:px-14 md:py-10 border-b border-gray-100 bg-white sticky top-0 z-20">
                  <div className="flex items-center space-x-5">
                    <div className="bg-red-600 p-3 md:p-4 rounded-2xl shadow-xl shadow-red-600/20">
                      <SafeIcon icon={FiMenu} className="w-6 h-6 md:w-8 md:h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-3xl md:text-5xl font-black text-green-800 italic uppercase tracking-tighter leading-none">The Menu</h3>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowMenuModal(false)}
                    className="bg-gray-100 text-gray-400 hover:text-red-600 hover:bg-red-50 p-4 md:p-6 rounded-2xl transition-all active:scale-90"
                  >
                    <SafeIcon icon={FiX} className="w-8 h-8 md:w-10 md:h-10" />
                  </button>
                </div>
                
                <div className="flex-1 overflow-y-auto overflow-x-hidden bg-gray-50 custom-scrollbar p-1 md:p-6">
                  <div className="flex justify-center bg-gray-50 min-h-full">
                    <div className="relative w-full max-w-4xl bg-white shadow-2xl rounded-2xl overflow-hidden">
                      <img 
                        src={menuImageUrl} 
                        alt="Got Them Dogs Full Menu" 
                        className="w-full h-auto block select-none img-fade-in"
                        loading="eager"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;